import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SESSION_KEY } from "constants/key";
import { EstQuoteDetailTire, ITireItemDetail } from "features/car_model/types";
import { getPructureProtectionApi } from "features/mst_option/api/optionApi";
import { PunctureProtection } from "features/mst_option/types/option";
import { uniqBy } from "lodash";
import { TOption } from "type";
import { TireResponse } from "features/car_model/api/carModelApi";
import {
  CategoryListItem,
  ITireInfo,
  MstItemDetailTireSelect,
  MstProcFeeTireItem,
} from "features/mst_proc_fee/types/proc-fee";
import { getItemDetailTireSelectApi } from "features/mst_proc_fee/api/procFeeApi";
import { fetchAndMapInventory } from "features/mst_proc_fee/utils/inventoryUtils";

// Constants
const PER_PAGE_DEFAULT = 20;
const SCROLL_THROTTLE_MS = 200;
const SCROLL_THRESHOLD_PX = 100;
const MAX_SELECTED_TIRES = 5;

interface ProcFeeDetail {
  value?: number;
  checked?: boolean;
}

interface ProcFeeDataItem {
  replac_labor_cost?: ProcFeeDetail;
  waste_tire_cost?: ProcFeeDetail;
  rubber_stem_valve_cost?: ProcFeeDetail;
  nitrogen_inflation_cost?: ProcFeeDetail;
  hub_rust_prevention_cost?: ProcFeeDetail;
  [key: string]: unknown;
}

interface MstProcFeeData {
  tbl_quote_detail_tire?: Array<Partial<EstQuoteDetailTire>>;
  proc_fees?: ProcFeeDataItem;
  cashback?: { puncture?: number; cashback_period?: number };
}

interface SessionDataType {
  previousPage: string;
  tireCategory: string | null;
  mstProcFee: MstProcFeeData;
  currentPage: number;
  storageSelectedMaker: TOption[];
  storageSelectedCategory: CategoryListItem[];
  storageTire: ITireInfo[];
}

/**
 * Get session storage data with default fallbacks
 */
const getSessionData = (): SessionDataType => {
  const previousPage = sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_FROM) || "";
  const tireCategory = sessionStorage.getItem(SESSION_KEY.TIRE_TYPE);
  const mstProcFee = JSON.parse(sessionStorage.getItem(SESSION_KEY.SELECTED_TIRE) || "{}");
  const currentPage = sessionStorage.getItem(SESSION_KEY.CURRENT_PAGE) || 1;
  const storageSelectedMaker: TOption[] =
    JSON.parse(sessionStorage.getItem(SESSION_KEY.SELECTED_MAKER) || "[]") ?? [];
  const storageSelectedCategory: CategoryListItem[] =
    JSON.parse(sessionStorage.getItem(SESSION_KEY.SELECTED_CATEGORY) || "[]") ?? [];
  const storageTire: ITireInfo[] =
    JSON.parse(sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_TIRE) || "[]") ?? [];

  return {
    previousPage,
    tireCategory,
    mstProcFee,
    currentPage: Number(currentPage),
    storageSelectedMaker,
    storageSelectedCategory,
    storageTire,
  };
};

/**
 * Process tire list with matching logic.
 *
 * Keyed by item_cd (stable) instead of array index so that restored selections
 * survive pagination — the same item_cd always maps to the same tire regardless
 * of which page it appears on.
 *
 * @param list - Array of tire items to process
 * @param mstProcFee - Master procedure fee data containing matched tires
 * @param onTireMatched - Callback function triggered when a tire matches a saved selection
 * @param isFirstPage - Whether this is the first page load (selections restored separately)
 * @returns Processed tire list with special discounts applied
 */
const processTireList = (
  list: ITireItemDetail[],
  mstProcFee: MstProcFeeData,
  onTireMatched: (tireId: string, matched: boolean) => void,
  isFirstPage: boolean = false,
): ITireItemDetail[] =>
  list
    .filter((tire) => tire.list_price !== null)
    .map((tire) => {
      const matchedTire = mstProcFee?.tbl_quote_detail_tire?.find(
        (item: Partial<EstQuoteDetailTire>) => item.tire_item_cd === tire.item_cd,
      );

      // Only call onTireMatched for subsequent pages; first-page selections are
      // restored in bulk by processApiResponse to avoid batching issues.
      if (matchedTire && !isFirstPage) {
        // Key by String(tire.id) — unique PK, unlike item_cd which may duplicate
        onTireMatched(String(tire.id), true);
      }

      return matchedTire ? { ...tire, special_discount: matchedTire.special_discount ?? 0 } : tire;
    });

/**
 * Custom hook to fetch and manage a paginated list of procedure fees.
 */
const useProcFeeList = () => {
  // Session data memoization
  const sessionData = useMemo(() => getSessionData(), []);

  // State declarations
  const [tireList, setTireList] = useState<ITireItemDetail[]>([]);
  const [originTireMakerList, setOriginTireMakerList] = useState<ITireItemDetail[]>([]);
  const [procFees, setProcFees] = useState<MstProcFeeTireItem>();
  const [makerList, setMakerList] = useState<TOption[]>([]);
  const [selectedMaker, setSelectedMaker] = useState<boolean[]>([]);
  const [categoryList, setCategoryList] = useState<CategoryListItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<boolean[]>([]);
  const [selectedTireIds, setSelectedTireIds] = useState<Record<string, boolean>>({});
  const [punctureProtection, setPunctureProtection] = useState<PunctureProtection[]>();
  const [tires, setTires] = useState<ITireInfo[]>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState(0);
  const [recommendFlag, setRecommendFlag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalSelectedTire, setTotalSelectedTire] = useState<number>(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const loadMoreRef = useRef(false);

  const totalPage = useMemo(() => Math.ceil(total / PER_PAGE_DEFAULT), [total]);

  // Fetch puncture protection on mount
  useEffect(() => {
    const fetchPunctureProtection = async () => {
      try {
        const response = await getPructureProtectionApi();
        setPunctureProtection(
          response.list.sort(
            (a: { coverage_amount_low: number }, b: { coverage_amount_low: number }) =>
              a.coverage_amount_low - b.coverage_amount_low,
          ),
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch puncture protection:", error);
      }
    };
    fetchPunctureProtection();
  }, []);

  /**
   * Handle tire matched callback — called by processTireList for load-more pages.
   * Keyed by String(tire.id) for uniqueness.
   */
  const handleTireMatched = useCallback((tireId: string, matched: boolean) => {
    setSelectedTireIds((prev: Record<string, boolean>) => ({
      ...prev,
      [tireId]: matched,
    }));

    if (matched) {
      setTotalSelectedTire((prevTotal) => prevTotal + 1);
    }
  }, []);

  /**
   * Format tire list by processing and filtering.
   * tempPage is no longer passed because selection is keyed by item_cd, not index.
   */
  const formatTireList = useCallback(
    (list: ITireItemDetail[], isFirstPage: boolean = false) =>
      processTireList(list, sessionData.mstProcFee, handleTireMatched, isFirstPage),
    [sessionData.mstProcFee, handleTireMatched],
  );

  /**
   * Build request parameters for API
   */
  const buildRequestParams = useCallback(() => {
    const params = {
      ...sessionData.storageTire[0],
      [sessionData.storageTire.length > 1 ? "item_cds" : "item_cd"]:
        sessionData.storageTire.length > 1
          ? sessionData.storageTire.map((el) => el.item_cd).join(",")
          : sessionData.storageTire[0].item_cd,
    } as Record<string, unknown>;

    if (sessionData.previousPage === "product-code") {
      delete params.tire_width;
      delete params.tire_loin_speed_mark;
      delete params.tire_aspect_ratio;
      delete params.tire_size;
    }

    if (sessionData.storageTire.length > 1) delete params.item_cd;

    return params;
  }, [sessionData]);

  /**
   * Auto-check all valid tires for product-code page.
   * Keyed by String(tire.id) — unique PK, not item_cd which may have duplicates.
   */
  const autoCheckAllTires = useCallback(
    (tireItems: ITireItemDetail[]): { ids: Record<string, boolean>; count: number } => {
      const ids: Record<string, boolean> = {};
      let count = 0;
      tireItems
        .filter((tire) => tire.list_price !== null)
        .forEach((tire) => {
          ids[String(tire.id)] = true;
          count += 1;
        });
      return { ids, count };
    },
    [],
  );

  /**
   * Process API response
   */
  const processApiResponse = useCallback(
    async (response: TireResponse, body: MstItemDetailTireSelect) => {
      const isFirstPage = body?.offset === 1;
      const formattedTires = formatTireList(response.list.mst_item_detail_tire, isFirstPage);

      if (isFirstPage) {
        setTotal(response.total);

        // Build initial selection state keyed by String(tire.id)
        let selectedTireIdsFromStorage: Record<string, boolean> = {};
        let totalSelectedFromStorage = 0;

        if (sessionData.previousPage === "product-code") {
          // Auto-select all valid tires on the product-code path
          const { ids, count } = autoCheckAllTires(response.list.mst_item_detail_tire);
          selectedTireIdsFromStorage = ids;
          totalSelectedFromStorage = count;
        } else if (sessionData.mstProcFee?.tbl_quote_detail_tire) {
          // Restore selections saved in session storage (back-navigation scenario).
          // Match via item_cd (stored in session), then key the result by String(tire.id).
          const selectedTireCodes = new Set(
            sessionData.mstProcFee.tbl_quote_detail_tire
              .filter((tire: Partial<EstQuoteDetailTire>) => tire.checked)
              .map((tire: Partial<EstQuoteDetailTire>) => tire.tire_item_cd),
          );
          response.list.mst_item_detail_tire.forEach((tire: ITireItemDetail) => {
            if (selectedTireCodes.has(tire.item_cd)) {
              selectedTireIdsFromStorage[String(tire.id)] = true;
              totalSelectedFromStorage += 1;
            }
          });
        }

        setSelectedTireIds(selectedTireIdsFromStorage);
        setTotalSelectedTire(totalSelectedFromStorage);

        // Fetch inventory and update tire list
        const tiresWithInventory = await fetchAndMapInventory(
          response.list.mst_item_detail_tire,
          formattedTires,
        );
        setTireList(tiresWithInventory);
        setOriginTireMakerList(response.list?.mst_item_detail_tire);

        // Initialize makers only once
        if (makerList.length === 0 && response.list.maker_list) {
          const makers = response.list.maker_list.map((item: { maker_name: string }) => ({
            label: item.maker_name,
            value: item.maker_name,
            selected: true,
          }));
          const newMakers = uniqBy(makers, "value");

          if (sessionData.storageSelectedMaker.length > 0) {
            setMakerList(sessionData.storageSelectedMaker);
            setSelectedMaker(
              sessionData.storageSelectedMaker.map((item: TOption) => item.selected as never),
            );
          } else {
            setMakerList(newMakers);
            setSelectedMaker(newMakers.map(() => true));
          }
        }

        // Initialize categories only once
        if (categoryList.length === 0 && response.list.category_list) {
          const categories = response.list.category_list.map((el: { category: string }) => ({
            category: el.category,
            selected: false,
          }));

          if (sessionData.storageSelectedCategory.length > 0) {
            setCategoryList(sessionData.storageSelectedCategory);
            setSelectedCategory(
              sessionData.storageSelectedCategory.map(
                (item: CategoryListItem) => item.selected as never,
              ),
            );
          } else {
            setCategoryList(categories);
            setSelectedCategory(categories.map(() => false));
          }
        }

        // Initialize procFees only once
        if (!procFees && response.list.mst_proc_fee) {
          setProcFees(response.list.mst_proc_fee);
        }
      } else {
        // Load more: append data with inventory
        setTotal(response.total);

        // Fetch inventory for new tires
        const newTiresWithInventory = await fetchAndMapInventory(
          response.list?.mst_item_detail_tire,
          formattedTires,
        );

        // Append new tires to existing list
        setTireList((prev: ITireItemDetail[]) => [...prev, ...newTiresWithInventory]);

        setOriginTireMakerList((prev: ITireItemDetail[]) => [
          ...prev,
          ...(response.list?.mst_item_detail_tire ?? []),
        ]);

        // Auto-check all new tires if previous page is "product-code"
        if (sessionData.previousPage === "product-code" && response.list?.mst_item_detail_tire) {
          const { ids, count } = autoCheckAllTires(response.list.mst_item_detail_tire);
          setSelectedTireIds((prev) => ({ ...prev, ...ids }));
          setTotalSelectedTire((prev) => prev + count);
        }
      }
    },
    [makerList.length, categoryList.length, procFees, formatTireList, sessionData, autoCheckAllTires],
  );

  /**
   * Main API call function
   */
  const getProcFees = useCallback(
    async (body: MstItemDetailTireSelect) => {
      setLoading(true);
      try {
        const prevScrollY = document.documentElement.scrollTop || document.body.scrollTop;
        const params = buildRequestParams();

        const response = (await getItemDetailTireSelectApi({
          ...body,
          ...params,
          tire_category: sessionData.tireCategory,
          category: recommendFlag ? "VAN" : "",
        })) as TireResponse;

        await processApiResponse(response, body);

        window.scrollTo(0, prevScrollY || 0);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch procedure fees:", error);
      } finally {
        setLoading(false);
      }
    },
    [buildRequestParams, processApiResponse, sessionData.tireCategory, recommendFlag],
  );

  /** Build API filter params from maker/category arrays */
  const buildFilterParams = useCallback(
    (maker: boolean[], category: boolean[]) => ({
      maker_names: makerList?.filter((_, i) => maker[i]).map((m) => m.value).join(","),
      categories: categoryList?.filter((_, i) => category[i]).map((c) => c.category).join(","),
    }),
    [makerList, categoryList],
  );

  /** Save filter state to storage */
  const saveToStorage = useCallback(
    (maker: boolean[], category: boolean[]) => {
      sessionStorage.setItem(
        SESSION_KEY.SELECTED_MAKER,
        JSON.stringify(makerList.map((el, i) => ({ ...el, selected: maker[i] }))),
      );
      sessionStorage.setItem(
        SESSION_KEY.SELECTED_CATEGORY,
        JSON.stringify(categoryList.map((el, i) => ({ ...el, selected: category[i] }))),
      );
    },
    [makerList, categoryList],
  );

  /** Memoized filter params for onLoadMore */
  const filterParams = useMemo(
    () => buildFilterParams(selectedMaker, selectedCategory),
    [buildFilterParams, selectedMaker, selectedCategory],
  );

  /** Apply filter. Overrides = values from modal; no overrides = use current state. */
  const handleFilter = useCallback(
    async (overrides?: { selectedMaker: boolean[]; selectedCategory: boolean[] }) => {
      const maker = overrides?.selectedMaker ?? selectedMaker;
      const category = overrides?.selectedCategory ?? selectedCategory;
      if (overrides) {
        setSelectedMaker(maker);
        setSelectedCategory(category);
      }
      setTotal(0);
      setPage(1);
      setOriginTireMakerList([]);
      loadMoreRef.current = false;
      await getProcFees({ offset: 1, limit: PER_PAGE_DEFAULT, ...buildFilterParams(maker, category) });
      saveToStorage(maker, category);
    },
    [selectedMaker, selectedCategory, buildFilterParams, getProcFees, saveToStorage],
  );

  /**
   * Load more handler
   */
  const onLoadMore = useCallback(async () => {
    if (
      page + 1 > totalPage ||
      originTireMakerList.length === 0 ||
      originTireMakerList.length < PER_PAGE_DEFAULT ||
      loadMoreRef.current
    ) {
      return;
    }

    loadMoreRef.current = true;
    setPage((prevPage) => prevPage + 1);

    const newParams: MstItemDetailTireSelect = {
      offset: page + 1,
      limit: PER_PAGE_DEFAULT,
      ...(filterParams.categories && { categories: filterParams.categories }),
      ...(filterParams.maker_names && { maker_names: filterParams.maker_names }),
    };

    try {
      await getProcFees(newParams);
    } finally {
      loadMoreRef.current = false;
    }
  }, [page, totalPage, originTireMakerList.length, filterParams, getProcFees]);

  const shouldLoadMore = useMemo(
    () => total > originTireMakerList.length && !loading && !loadMoreRef.current,
    [total, originTireMakerList.length, loading],
  );

  /**
   * Sync page to sessionStorage when it changes
   */
  //   useEffect(() => {
  //     if (isInitialized && page > 0) {
  //       sessionStorage.setItem(SESSION_KEY.CURRENT_PAGE, String(page));
  //     }
  //   }, [page, isInitialized]);

  /**
   * Scroll event listener with debouncing
   */
  useEffect(() => {
    if (!shouldLoadMore) return undefined;

    let scrollTimeout: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        const { scrollHeight } = document.documentElement;
        const scrollTop = window.innerHeight + window.scrollY;
        const threshold = scrollHeight - SCROLL_THRESHOLD_PX;

        if (scrollTop >= threshold && shouldLoadMore) {
          onLoadMore();
        }
      }, SCROLL_THROTTLE_MS);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return (): void => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [shouldLoadMore, onLoadMore]);

  /**
   * Initialize on mount and restore state - Only run once
   */
  useEffect((): (() => void) => {
    let isMounted = true;

    // Early return if already initialized
    if (isInitialized) {
      return (): void => {
        isMounted = false;
      };
    }

    const init = async (): Promise<void> => {
      if (sessionData.storageTire.length === 0 || !isMounted) return;
      //   setPage(1);
      //   sessionStorage.setItem(SESSION_KEY.CURRENT_PAGE, "1");

      // Batch synchronous state updates
      setTires(sessionData.storageTire);

      const recommendFlagRaw = sessionStorage.getItem(SESSION_KEY.RECOMMEND_FLAG);
      const storageRecommendFlag = recommendFlagRaw ? JSON.parse(recommendFlagRaw) : false;
      setRecommendFlag(storageRecommendFlag);

      // Prepare filter params
      const makerParams = sessionData.storageSelectedMaker
        .filter((el: TOption) => el.selected)
        .map((el: TOption) => el.value)
        .join(",");
      const categoryParams = sessionData.storageSelectedCategory
        .filter((el: CategoryListItem) => el.selected)
        .map((el: CategoryListItem) => el.category)
        .join(",");

      // Fetch pages sequentially to avoid race conditions with inventory API
      // This ensures inventory data is fetched and mapped correctly for each page
      for (let i = 1; i <= sessionData.currentPage && isMounted; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await getProcFees({
          offset: i,
          limit: PER_PAGE_DEFAULT,
          maker_names: makerParams,
          categories: categoryParams,
        });
      }
      // Only fetch first page on reload
      // TODO: confirm this is needed
      //   await getProcFees({
      //     offset: 1,
      //     limit: PER_PAGE_DEFAULT,
      //     maker_names: makerParams,
      //     categories: categoryParams,
      //   });

      // Update procFees with stored values after all data is loaded
      if (isMounted) {
        setProcFees((prev: MstProcFeeTireItem | undefined) => ({
          ...prev,
          replac_labor_cost:
            sessionData.mstProcFee?.proc_fees?.replac_labor_cost?.value ??
            prev?.replac_labor_cost ??
            0,
          waste_tire_cost:
            sessionData.mstProcFee?.proc_fees?.waste_tire_cost?.value ?? prev?.waste_tire_cost ?? 0,
          rubber_stem_valve_cost:
            sessionData.mstProcFee?.proc_fees?.rubber_stem_valve_cost?.value ??
            prev?.rubber_stem_valve_cost ??
            0,
          nitrogen_inflation_cost:
            sessionData.mstProcFee?.proc_fees?.nitrogen_inflation_cost?.value ??
            prev?.nitrogen_inflation_cost ??
            0,
          hub_rust_prevention_cost:
            sessionData.mstProcFee?.proc_fees?.hub_rust_prevention_cost?.value ??
            prev?.hub_rust_prevention_cost ??
            0,
        }));

        setIsInitialized(true);
      }
    };

    init();

    return (): void => {
      isMounted = false;
    };
  }, []); // Empty dependencies - run only once on mount

  return {
    procFees,
    setProcFees,
    tires,
    setTires,
    tireList,
    setTireList,
    makerList,
    setMakerList,
    categoryList,
    selectedCategory,
    setSelectedCategory,
    onLoadMore,
    loading,
    handleFilter,
    selectedTireIds,
    setSelectedTireIds,
    selectedMaker,
    setSelectedMaker,
    mstProcFee: sessionData.mstProcFee,
    totalSelectedTire,
    setTotalSelectedTire,
    page,
    recommendFlag,
    setRecommendFlag,
    punctureProtection,
    tireCategory: sessionData.tireCategory,
    maxSelectedTires: MAX_SELECTED_TIRES,
  };
};

export default useProcFeeList;
